/**
 * Shared module-icon registry.
 *
 * Single source of truth for "the icon that represents a module". The
 * left-hand Sidebar is driven by ``NAV`` in ``nav-spec.js`` — every icon
 * we use elsewhere (dashboard chips, callout tiles, empty-states, page
 * headers, prebuilt sections, etc.) MUST resolve through this helper so
 * a rename in ``nav-spec.js`` automatically fans out.
 *
 * Usage
 * -----
 *      import { moduleIcon, ModuleIcon } from "@/lib/moduleIcons";
 *
 *      const Icon = moduleIcon("sense_ai");
 *      <Icon className="w-4 h-4" />
 *
 *      // or the component wrapper:
 *      <ModuleIcon module="dashboards" className="w-4 h-4" />
 *
 * When adding a new nav entry in ``nav-spec.js``, no change is required
 * here — the mapping is derived lazily via ``NAV`` traversal.
 */
import React from "react";
import { HelpCircle } from "lucide-react";
import { NAV } from "@/lib/nav-spec";

// Flatten NAV to a { key → icon, module → icon } lookup once at module
// import. Handles group entries (which have children) and leaf entries
// alike. Both `key` and `module` are indexed so callers can look up by
// either identifier — some legacy call-sites still pass NAV `key`s.
const _cache = (() => {
    const byKey = {};
    const byModule = {};
    const visit = (node) => {
        if (!node) return;
        if (node.icon) {
            if (node.key)    byKey[node.key] = node.icon;
            if (node.module) byModule[node.module] = node.icon;
        }
        (node.children || []).forEach(visit);
    };
    NAV.forEach(visit);
    return { byKey, byModule };
})();

// Alias table — for call-sites where the module name we render doesn't
// exactly match the NAV `module` id but semantically means the same
// thing. Add aliases here rather than adding hardcoded per-page imports.
const ALIASES = {
    "senseai":            "sense_ai",
    "sense-ai":           "sense_ai",
    "senseAi":            "sense_ai",
    "connect_data":       "data",
    "connect-data":       "data",
    "add_source":         "data",
    "add-source":         "data",
    "prebuilt":           "dashboards",
    "prebuilt_dashboards": "dashboards",
    "dashboard":          "dashboards",
    "app_catalog":        "marketplace",
    "app-catalog":        "marketplace",
    "my-apps":            "my_apps",
    "data_source":        "data_source_group",
    "data-source":        "data_source_group",
    "app_studio":         "app_studio",
    "app-studio":         "app_studio",
    "yaml_connectors":    "yaml_connectors",
    "yaml-connectors":    "yaml_connectors",
    "platform_connectors": "platform_connectors",
    "platform_templates":  "platform_templates",
    "data_models":        "data_models",
    "data-models":        "data_models",
    "unified":            "data_models",
    // iter-2026 — Insights merge. Keep the old module keys resolving to
    // the new icon so any residual icon lookup still renders cleanly.
    "operations":         "usage_reports",
    "reports":            "usage_reports",
    "insights_group":     "usage_reports",
    "usage-reports":      "usage_reports",
};

/** Return the lucide-react icon component associated with `key`. Falls
 *  back to ``HelpCircle`` (never crashes). */
export function moduleIcon(key) {
    if (!key) return HelpCircle;
    const k = String(key).trim();
    const resolved = ALIASES[k] || k;
    return _cache.byModule[resolved] || _cache.byKey[resolved] || HelpCircle;
}

/** React wrapper. Accepts a `module` prop (alias-friendly) plus any
 *  props the lucide icon accepts (className, size, strokeWidth, …). */
export function ModuleIcon({ module, ...rest }) {
    const Icon = moduleIcon(module);
    return <Icon {...rest} />;
}

export default moduleIcon;
